<template>
  <div class="resolution_analytics">
    <table v-if="mode === 'list'" class="list_table">
      <tr>
        <th width="22%">{{ $t('common.device') }}</th>
        <th width="42%">{{ $t('common.resolution') }}</th>
        <th width="18%">UV</th>
        <th>{{ $t('common.unit.ratio') }}</th>
      </tr>
      <template v-if="allItems.length">
        <tr class="item-row" v-for="(item, index) in listItems" :key="index">
          <td>{{ item.device }}</td>
          <td>{{ item.res }}</td>
          <td>{{ item.uv | thousand }}</td>
          <td>{{ item.uvPercentage | fixed(1) }}%</td>
        </tr>
      </template>
      <template v-else>
        <tr>
          <td colspan="4">
            <analytics-no-data :height="260"/>
          </td>
        </tr>
      </template>
    </table>
    <div v-else class="chart_box">
      <app-chart chartType="scatter" v-if="chartOptions && allItems.length"
        :height="334"
        :chartOptions="chartOptions">
      </app-chart>
      <analytics-no-data v-else
        :height="334"/>
    </div>
  </div>
</template>

<script>
import { sum, getPercentage, thousand } from '@/libs/utils'
import { getTooltipHtml } from '@/libs/chart-helper'
import AppChart from '@/components/chart'
import AnalyticsNoData from '@/components/analytics/AnalyticsNoData'

export default {
  name: 'ResolutionAnalytics',
  components: {
    AppChart,
    AnalyticsNoData
  },
  props: [
    'mode',
    'resolutionData',
    'device'
  ],
  computed: {
    allItems () {
      let items = this.resolutionData.items

      if (!items) {
        return []
      }

      if (this.device !== null) {
        items = items.filter(item => item.device === this.device)
      }
      return items
    },
    chartOptions () {
      const upperFirst = this.$options.filters.upperFirst
      const deviceData = {
        desktop: {
          items: []
        },
        tablet: {
          items: []
        },
        phone: {
          items: []
        }
      }

      for (const item of this.allItems) {
        deviceData[item.device].items.push([item.width, item.height, item.uv])
      }

      const minSymbolSize = 3
      const maxSymbolSize = 80
      const series = []
      const colorSet = {
        desktop: '#1199af',
        tablet: '#bbb',
        phone: '#9fce12'
      }

      for (const [device, data] of Object.entries(deviceData)) {
        const maxUv = this.resolutionData.maxUv

        if (data.items.length) {
          series.push({
            type: 'scatter',
            name: upperFirst(device),
            data: data.items,
            symbolSize (value) {
              const uv = value[value.length - 1]
              return Math.floor(uv / maxUv * (maxSymbolSize - minSymbolSize)) + minSymbolSize
            },
            itemStyle: {
              color: colorSet[device],
              opacity: 0.7
            }
          })
        }
      }

      return {
        series,
        tooltip: {
          formatter (params) {
            const content = [
              ['width : ', thousand(params.data[0])],
              ['height : ', thousand(params.data[1])],
              ['UV : ', thousand(params.data[2])]
            ]
            return getTooltipHtml(content, upperFirst(params.seriesName))
          }
        },
        grid: {
          containLabel: true,
          left: 20,
          bottom: 20,
          right: 20
        },
        xAxis: {
          name: 'Width',
          nameLocation: 'center',
          nameTextStyle: {
            color: '#aaa'
          },
          nameGap: 30,
          minInterval: 200,
          maxInterval: 500
        },
        yAxis: {
          name: 'Height',
          nameLocation: 'center',
          nameTextStyle: {
            color: '#aaa'
          },
          nameGap: 40,
          minInterval: 200,
          maxInterval: 500
        }
      }
    },
    listItems () {
      const totalUv = this.resolutionData.totalUv
      let allItems = this.allItems

      if (!allItems.length) {
        return []
      }

      const topItems = allItems.slice(0, 5).map(item => {
        return {
          device: this.$options.filters.upperFirst(item.device),
          res: `${item.width} * ${item.height}`,
          uv: item.uv,
          uvPercentage: getPercentage(item.uv, totalUv)
        }
      })

      const etcItems = allItems.slice(5)

      if (etcItems.length) {
        const etcTotalUv = sum(etcItems, 'uv')
        topItems.push({
          device: 'etc',
          res: 'etc',
          uv: etcTotalUv,
          uvPercentage: getPercentage(etcTotalUv, totalUv)
        })
      }
      return topItems
    }
  }
}
</script>

<style lang="scss">
.resolution_analytics {
  .chart_box {
    margin-top: 10px;
  }
  .list_table {
    border-collapse: collapse;
    width: 100%;
    text-align: center;
    margin-top: 24px;

    th {
      font-size: 12px;
      color: #444;
      padding-bottom: 6px;
      font-weight: 400;
    }

    td {
      font-size: 13px;
      color: #2f2f2f;
      padding: 15px 0;
      font-weight: 500;
    }

    tr {
      border-bottom: 1px dashed #f4f4f1;

      &:first-child {
        border-bottom: 1px solid #bbb;
      }

      &.item-raw:last-child {
        border-bottom: 1px solid #d7d7d7;
      }
    }
  }
}
</style>
